/**
 * React Query Hooks for View Data
 *
 * Provides React Query hooks for fetching and mutating view data
 * with caching, optimistic updates, and automatic refetching.
 *
 * Based on the original MyVault useViewData.ts patterns.
 */

import { useEffect } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
  Query,
} from "@tanstack/react-query";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import {
  subscribeWorkspaceObjectChanges,
  type WorkspaceObjectChange,
} from "../services/workspaceObjectChanges";
import {
  createViewService,
  type CanvasWriteContext,
  type ViewQueryParams,
  type ProcessedViewData,
  type BulkOperationResult,
} from "../services/viewService";
import type { ViewFilter } from "../types";

/**
 * Query key factory for view data
 * Following the pattern from original MyVault
 */
export const viewDataKeys = {
  all: ["viewData"] as const,
  lists: () => [...viewDataKeys.all, "list"] as const,
  list: (workspaceId: string) =>
    [...viewDataKeys.lists(), workspaceId] as const,
  details: () => [...viewDataKeys.all, "detail"] as const,
  detail: (workspaceId: string, viewId: string) =>
    [...viewDataKeys.details(), workspaceId, viewId] as const,
  data: (workspaceId: string, viewId: string) =>
    [...viewDataKeys.all, "data", workspaceId, viewId] as const,
  query: (workspaceId: string, viewId: string, params?: ViewQueryParams) =>
    [...viewDataKeys.data(workspaceId, viewId), "query", params] as const,
};

const viewAggregateKeys = {
  all: ["viewAggregate"] as const,
  view: (workspaceId: string, viewId: string) =>
    ["viewAggregate", workspaceId, viewId] as const,
};

export function viewUsesTable(view: unknown, tableId: string): boolean {
  if (!view || typeof view !== "object") return false;
  const definition = view as {
    focus_table_id?: string | number;
    joins?: Array<{
      source_table_id?: string | number;
      target_table_id?: string | number;
      schema_relationship?: {
        from_table_id?: string | number;
        to_table_id?: string | number;
      };
    }>;
  };
  if (String(definition.focus_table_id ?? "") === tableId) return true;
  return (definition.joins ?? []).some((join) => {
    const relationship = join.schema_relationship;
    return [
      join.source_table_id,
      join.target_table_id,
      relationship?.from_table_id,
      relationship?.to_table_id,
    ].some((id) => id !== undefined && String(id) === tableId);
  });
}

function getCachedViewDefinition(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
  viewId: string,
): unknown {
  const detail = queryClient.getQueryData(
    viewDataKeys.detail(workspaceId, viewId),
  );
  if (detail) return detail;

  const viewList = queryClient.getQueryData<unknown>(
    viewDataKeys.list(workspaceId),
  );
  const views = Array.isArray(viewList)
    ? viewList
    : viewList &&
        typeof viewList === "object" &&
        Array.isArray((viewList as { items?: unknown[] }).items)
      ? (viewList as { items: unknown[] }).items
      : [];
  return views.find((candidate) => {
    if (!candidate || typeof candidate !== "object") return false;
    return String((candidate as { id?: string | number }).id ?? "") === viewId;
  });
}

/** Load uncached view metadata through its shared React Query key. */
export async function loadViewDefinitionForObjectChange(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
  viewId: string,
  loadViewDefinition: (viewId: string) => Promise<unknown>,
): Promise<unknown> {
  const cached = getCachedViewDefinition(queryClient, workspaceId, viewId);
  if (cached) return cached;

  return queryClient.fetchQuery({
    queryKey: viewDataKeys.detail(workspaceId, viewId),
    queryFn: () => loadViewDefinition(viewId),
    staleTime: 30_000,
  });
}

export async function invalidateViewQueriesForObjectChange(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
  change: WorkspaceObjectChange,
  loadViewDefinition?: (viewId: string) => Promise<unknown>,
): Promise<void> {
  if (change.scope === "workspace") {
    const activeQueries = [
      ...queryClient
        .getQueryCache()
        .findAll({ queryKey: viewDataKeys.all })
        .filter((query) => query.queryKey[1] === "data"),
      ...queryClient
        .getQueryCache()
        .findAll({ queryKey: viewAggregateKeys.all })
        .filter((query) => query.queryKey[0] === "viewAggregate"),
    ].filter(
      (query) =>
        query.isActive() &&
        (query.queryKey[0] === "viewData"
          ? query.queryKey[2] === workspaceId
          : query.queryKey[1] === workspaceId),
    );
    await Promise.all(
      activeQueries.map((query) =>
        queryClient.invalidateQueries({ queryKey: query.queryKey }),
      ),
    );
    return;
  }

  const viewIdsToRefresh = new Set<string>();
  const changedViewIds = new Set<string>();
  for (const object of change.changedObjects) {
    if (object.type === "view") {
      changedViewIds.add(object.id);
      viewIdsToRefresh.add(object.id);
    } else if (object.type === "row" && object.viewId) {
      viewIdsToRefresh.add(object.viewId);
    }
  }

  const tableIds = new Set(
    change.changedObjects.flatMap((object) =>
      object.type === "table"
        ? [object.id]
        : object.type === "row" && object.tableId
          ? [object.tableId]
          : [],
    ),
  );
  if (tableIds.size === 0) {
    if (changedViewIds.size > 0) {
      await queryClient.invalidateQueries({
        queryKey: viewDataKeys.list(workspaceId),
      });
      for (const viewId of changedViewIds) {
        await queryClient.invalidateQueries({
          queryKey: viewDataKeys.detail(workspaceId, viewId),
        });
      }
    }
    await Promise.all(
      [...viewIdsToRefresh].flatMap((viewId) => [
        queryClient.invalidateQueries({
          queryKey: viewDataKeys.data(workspaceId, viewId),
        }),
        queryClient.invalidateQueries({
          queryKey: viewAggregateKeys.view(workspaceId, viewId),
        }),
      ]),
    );
    return;
  }

  const activeDataQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: viewDataKeys.all })
    .filter((query) => {
      const queryKey = query.queryKey;
      return (
        query.isActive() &&
        queryKey[1] === "data" &&
        queryKey[2] === workspaceId
      );
    });

  const activeAggregateQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: viewAggregateKeys.all })
    .filter(
      (query) =>
        query.isActive() &&
        query.queryKey[1] === workspaceId &&
        typeof query.queryKey[2] === "string",
    );
  const activeViewIds = new Set(
    [...activeDataQueries, ...activeAggregateQueries]
      .map((query) =>
        String(
          query.queryKey[0] === "viewData"
            ? (query.queryKey[3] ?? "")
            : (query.queryKey[2] ?? ""),
        ),
      )
      .filter(Boolean),
  );
  const definitions = await Promise.all(
    [...activeViewIds].map(async (viewId) => {
      const cachedView = getCachedViewDefinition(
        queryClient,
        workspaceId,
        viewId,
      );
      if (cachedView || !loadViewDefinition) {
        return [viewId, cachedView] as const;
      }

      try {
        const view = await loadViewDefinitionForObjectChange(
          queryClient,
          workspaceId,
          viewId,
          loadViewDefinition,
        );
        return [viewId, view] as const;
      } catch {
        // A missing/inaccessible view must not prevent other affected views
        // from refreshing after the same workspace change.
        return [viewId, null] as const;
      }
    }),
  );
  for (const [viewId, view] of definitions) {
    if (view && [...tableIds].some((tableId) => viewUsesTable(view, tableId))) {
      viewIdsToRefresh.add(viewId);
    }
  }

  if (changedViewIds.size > 0) {
    await queryClient.invalidateQueries({
      queryKey: viewDataKeys.list(workspaceId),
    });
    for (const viewId of changedViewIds) {
      await queryClient.invalidateQueries({
        queryKey: viewDataKeys.detail(workspaceId, viewId),
      });
    }
  }
  await Promise.all(
    [...viewIdsToRefresh].flatMap((viewId) => [
      queryClient.invalidateQueries({
        queryKey: viewDataKeys.data(workspaceId, viewId),
      }),
      queryClient.invalidateQueries({
        queryKey: viewAggregateKeys.view(workspaceId, viewId),
      }),
    ]),
  );
}

/** One React Query subscriber for the workspace object-change signal. */
export function useWorkspaceObjectChangeQueryInvalidation(
  workspaceId: string,
): void {
  const queryClient = useQueryClient();
  const authFetch = useAuthFetch();
  useEffect(
    () =>
      subscribeWorkspaceObjectChanges(workspaceId, (change) => {
        void invalidateViewQueriesForObjectChange(
          queryClient,
          workspaceId,
          change,
          (viewId) => createViewService(authFetch, workspaceId).getView(viewId),
        );
      }),
    [authFetch, queryClient, workspaceId],
  );
}

/**
 * Hook to fetch all views for the current workspace
 */
export function useViewsQuery() {
  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  return useQuery({
    queryKey: viewDataKeys.list(workspaceId),
    queryFn: () => createViewService(authFetch, workspaceId).getViews(),
    enabled: !!workspaceId,
    staleTime: 1000 * 30, // 30 seconds
    gcTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch a single view definition
 */
export function useViewQuery(viewId: string | number | null) {
  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";
  const viewIdStr = viewId?.toString() || "";

  return useQuery({
    queryKey: viewDataKeys.detail(workspaceId, viewIdStr),
    queryFn: () => createViewService(authFetch, workspaceId).getView(viewIdStr),
    enabled: !!workspaceId && !!viewId,
    staleTime: 1000 * 30, // 30 seconds
    gcTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch view data with filters, sorting, and pagination
 */
export function useViewDataQuery(
  viewId: string | number | null,
  params?: ViewQueryParams,
) {
  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";
  const viewIdStr = viewId?.toString() || "";

  return useQuery({
    queryKey: viewDataKeys.query(workspaceId, viewIdStr, params),
    queryFn: () =>
      createViewService(authFetch, workspaceId).queryViewData(
        viewIdStr,
        params,
      ),
    enabled: !!workspaceId && !!viewId,
    staleTime: 1000 * 30, // 30 seconds
    gcTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to create a new row in a view
 */
export function useCreateRowMutation() {
  const queryClient = useQueryClient();
  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  return useMutation({
    mutationFn: ({
      viewId,
      data,
      context,
    }: {
      viewId: string;
      data: Record<string, unknown>;
      context?: CanvasWriteContext;
    }) =>
      createViewService(authFetch, workspaceId).createRow(
        viewId,
        data,
        context,
      ),
    onSuccess: (
      _data: unknown,
      variables: { viewId: string; data: Record<string, unknown> },
    ) => {
      // Invalidate and refetch view data after successful creation
      queryClient.invalidateQueries({
        queryKey: viewDataKeys.data(workspaceId, variables.viewId),
      });
    },
    onError: (error: Error) => {
      console.error("Failed to create row:", error);
    },
  });
}

/**
 * Conflict error info returned when a 409 is detected during row update.
 */
export interface ConflictError {
  status: 409;
  message: string;
  currentRow: Record<string, unknown>;
  yourData: Record<string, unknown>;
  rowId: string;
  viewId: string;
}

/**
 * Hook to update a single row in a view with optimistic updates
 * and optimistic locking via _version.
 */
export function useUpdateRowMutation() {
  const queryClient = useQueryClient();
  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  return useMutation({
    mutationFn: ({
      viewId,
      rowId,
      data,
      context,
    }: {
      viewId: string;
      rowId: string;
      data: Record<string, unknown>;
      context?: CanvasWriteContext;
    }) => {
      // If _version is not already in data, look it up from the cached row
      let dataWithVersion = data;
      if (!("_version" in data)) {
        const cachedQueries = queryClient.getQueriesData({
          queryKey: viewDataKeys.data(workspaceId, viewId),
        });
        for (const [, cached] of cachedQueries) {
          const cachedData = cached as ProcessedViewData | undefined;
          if (!cachedData?.rows) continue;
          const row = cachedData.rows.find(
            (r) => r._rowId === rowId || r.__row_id === rowId,
          );
          if (row && row._version !== undefined) {
            dataWithVersion = { ...data, _version: row._version };
            break;
          }
        }
      }
      return createViewService(authFetch, workspaceId).updateRow(
        viewId,
        rowId,
        dataWithVersion,
        context,
      );
    },
    onMutate: async ({
      viewId,
      rowId,
      data,
    }: {
      viewId: string;
      rowId: string;
      data: Record<string, unknown>;
    }): Promise<{
      previousData: [readonly unknown[], unknown][];
      previousVersion?: number;
    }> => {
      // Cancel any outgoing refetches for this view's data
      await queryClient.cancelQueries({
        queryKey: viewDataKeys.data(workspaceId, viewId),
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueriesData({
        queryKey: viewDataKeys.data(workspaceId, viewId),
      });

      // Grab the current row's _version before optimistic update
      let previousVersion: number | undefined;
      for (const [, cached] of previousData) {
        const cachedData = cached as ProcessedViewData | undefined;
        if (!cachedData?.rows) continue;
        const row = cachedData.rows.find(
          (r) => r._rowId === rowId || r.__row_id === rowId,
        );
        if (row && row._version !== undefined) {
          previousVersion = row._version as number;
          break;
        }
      }

      // Optimistically update all queries for this view's data
      queryClient.setQueriesData(
        { queryKey: viewDataKeys.data(workspaceId, viewId) },
        (old: unknown) => {
          const oldData = old as ProcessedViewData | undefined;
          if (!oldData?.rows) return old;

          return {
            ...oldData,
            rows: oldData.rows.map((row) =>
              row._rowId === rowId || row.__row_id === rowId
                ? { ...row, ...data }
                : row,
            ),
          };
        },
      );

      return { previousData, previousVersion };
    },
    onError: (
      error: Error,
      variables: {
        viewId: string;
        rowId: string;
        data: Record<string, unknown>;
      },
      context:
        | {
            previousData: [readonly unknown[], unknown][];
            previousVersion?: number;
          }
        | undefined,
    ) => {
      // Rollback on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      // Don't log 409 conflicts -- handled by conflict resolution dialog
      if ((error as any)?.status !== 409) {
        console.error("Failed to update row:", error);
      }
    },
    onSettled: (
      _data: unknown,
      error: Error | null,
      variables: {
        viewId: string;
        rowId: string;
        data: Record<string, unknown>;
      },
    ) => {
      // Skip refetch on 409 conflict -- the conflict dialog will handle resolution
      if (error && (error as any).status === 409) {
        return;
      }
      // Always refetch after mutation
      queryClient.invalidateQueries({
        queryKey: viewDataKeys.data(workspaceId, variables.viewId),
      });
    },
  });
}

/**
 * Hook to delete a row from a view with optimistic updates
 */
export function useDeleteRowMutation() {
  const queryClient = useQueryClient();
  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  return useMutation({
    mutationFn: ({
      viewId,
      rowId,
      context,
    }: {
      viewId: string;
      rowId: string;
      context?: CanvasWriteContext;
    }) =>
      createViewService(authFetch, workspaceId).deleteRow(
        viewId,
        rowId,
        context,
      ),
    onMutate: async ({
      viewId,
      rowId,
    }: {
      viewId: string;
      rowId: string;
    }): Promise<{ previousData: [readonly unknown[], unknown][] }> => {
      // Cancel any outgoing refetches for this view's data
      await queryClient.cancelQueries({
        queryKey: viewDataKeys.data(workspaceId, viewId),
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueriesData({
        queryKey: viewDataKeys.data(workspaceId, viewId),
      });

      // Optimistically remove the row from all queries
      queryClient.setQueriesData(
        { queryKey: viewDataKeys.data(workspaceId, viewId) },
        (old: unknown) => {
          const oldData = old as ProcessedViewData | undefined;
          if (!oldData?.rows) return old;

          return {
            ...oldData,
            rows: oldData.rows.filter(
              (row) => row._rowId !== rowId && row.__row_id !== rowId,
            ),
            totalCount: Math.max(0, oldData.totalCount - 1),
          };
        },
      );

      return { previousData };
    },
    onError: (
      error: Error,
      variables: { viewId: string; rowId: string },
      context: { previousData: [readonly unknown[], unknown][] } | undefined,
    ) => {
      // Rollback on error (restore optimistically removed row)
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      // 409 with dependents is a referential integrity block, not a generic failure
      if ((error as any)?.status === 409) {
        console.warn("Delete blocked by referential integrity:", error);
      } else {
        console.error("Failed to delete row:", error);
      }
    },
    onSettled: (
      _data: unknown,
      error: Error | null,
      variables: { viewId: string; rowId: string },
    ) => {
      // Skip refetch on 409 -- the row was never deleted, rollback already restored it
      if (error && (error as any).status === 409) {
        return;
      }
      // Always refetch after mutation
      queryClient.invalidateQueries({
        queryKey: viewDataKeys.data(workspaceId, variables.viewId),
      });
    },
  });
}

/**
 * Hook to export view data
 */
export function useExportViewMutation() {
  // "raw" so the CSV body is handed back as a Response. The default parses
  // every response as JSON, which throws on a CSV body and surfaces the raw
  // parser message -- quoting the first bytes of the user's own data -- instead
  // of downloading a file.
  const authFetch = useAuthFetch("raw");
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  return useMutation({
    mutationFn: ({
      viewId,
      filters,
    }: {
      viewId: string;
      filters?: ViewFilter[];
    }) => createViewService(authFetch, workspaceId).exportView(viewId, filters),
    onSuccess: (
      blob: Blob,
      variables: { viewId: string; filters?: ViewFilter[] },
    ) => {
      // Create a download link for the exported file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `view-${variables.viewId}-export.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error: Error) => {
      console.error("Failed to export view:", error);
    },
  });
}

/**
 * Hook to bulk delete rows from a view
 */
export function useBulkDeleteMutation() {
  const queryClient = useQueryClient();
  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  return useMutation({
    mutationFn: ({
      viewId,
      rowIds,
    }: {
      viewId: string;
      rowIds: string[];
    }): Promise<BulkOperationResult> =>
      createViewService(authFetch, workspaceId).bulkDeleteRows(viewId, rowIds),
    onSuccess: (
      _data: BulkOperationResult,
      variables: { viewId: string; rowIds: string[] },
    ) => {
      queryClient.invalidateQueries({
        queryKey: viewDataKeys.data(workspaceId, variables.viewId),
      });
    },
    onError: (error: Error) => {
      console.error("Failed to bulk delete rows:", error);
    },
  });
}

/**
 * Hook to import a CSV file into a view
 */
export function useImportCsvMutation() {
  const queryClient = useQueryClient();
  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  return useMutation({
    mutationFn: ({
      viewId,
      file,
    }: {
      viewId: string;
      file: File;
    }): Promise<BulkOperationResult> =>
      createViewService(authFetch, workspaceId).importCsv(viewId, file),
    onSuccess: (
      _data: BulkOperationResult,
      variables: { viewId: string; file: File },
    ) => {
      queryClient.invalidateQueries({
        queryKey: viewDataKeys.data(workspaceId, variables.viewId),
      });
    },
    onError: (error: Error) => {
      console.error("Failed to import CSV:", error);
    },
  });
}

/**
 * Hook to invalidate all view data caches for the current workspace
 * Useful when switching workspaces or after major data changes
 */
export function useInvalidateViewData() {
  const queryClient = useQueryClient();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  return () => {
    queryClient.invalidateQueries({
      queryKey: viewDataKeys.all,
      predicate: (query: Query) => {
        // Only invalidate queries for the current workspace
        const key = query.queryKey as string[];
        return key.includes(workspaceId);
      },
    });
  };
}
